import { useContext, useEffect, useMemo, useState } from "react";
import { GeaoContext } from "../../../GeaoContextProvider";
import { getOfficeReportsToOptions } from "../../../../../api/search";

/**
 * Custom React hook that fetches and formats office report-to options for selection
 * 
 * This hook retrieves the parent agency and its associated branches based on the current
 * agent interview context, then formats them into a sorted list of selectable options
 * for the "Office you report to" dropdown.
 * 
 * @returns {Object} Object with the following properties:
 *   - branchOptions: {Array<Object>|undefined} Array of office option objects with the following structure:
 *     - value: {string} The agency ID
 *     - label: {string} Formatted display text in the format "AgencyID - AgencyName"
 *     - interviewId: {string} The interview ID associated with the office
 *     Returns undefined while data is loading or if no data is available.
 *   - areOfficesLoading: {boolean} True when the offices search API call is in progress, false otherwise
 * 
 * @example
 * const { branchOptions, areOfficesLoading } = useOfficeReportsToOptions();
 * // Returns: {
 * //   branchOptions: [
 * //     { value: "12345", label: "12345 - Main Agency", interviewId: "abc123" },
 * //     { value: "12346", label: "12346 - Branch Office", interviewId: "def456" }
 * //   ],
 * //   areOfficesLoading: false
 * // }
 */
export default function useOfficeReportsToOptions() {
    const { agentInterview } = useContext(GeaoContext);

    /** @type {[Array<Object>|undefined, Function]} State for storing the list of office option responses */
    const [officeReportsToOptions, setOfficeReportsToOptions] = useState();

    /** @type {[boolean, Function]} State for tracking if the offices search API call is in progress */
    const [areOfficesLoading, setAreOfficesLoading] = useState(false);

    /**
     * Effect hook to fetch office report-to options on component mount
     * 
     * Finds the parent agency ID from the agent interview relationships,
     * then makes an API call to fetch the parent agency and all branches
     * associated with it for the "Office you report to" dropdown.
     */
    useEffect(() => {
        const parentAgencyId = agentInterview?.relationships?.find(x => x.type === "ParentAgency")?.sourceOfBusiness;

        if (!parentAgencyId) return;

        setAreOfficesLoading(true);
        getOfficeReportsToOptions(response => {
            setOfficeReportsToOptions(response);
            setAreOfficesLoading(false);
        });
    }, [agentInterview]);

    const branchOptions = useMemo(() => {
        if (!officeReportsToOptions || officeReportsToOptions.length === 0) return;

        /**
         * Helper function to format agency information into a display label
         * @param {Object} searchResponse - Agency search response object
         * @param {string} searchResponse.agencyId - The agency ID
         * @param {string} searchResponse.agencyName - The agency name
         * @returns {string} Formatted label in the format "AgencyID - AgencyName"
         */
        const getLabel = (searchResponse) => `${searchResponse.agencyId} - ${searchResponse.agencyName}`;

        return officeReportsToOptions.map(response => ({
            value: response.agencyId,
            label: getLabel(response),
            interviewId: response.interviewId
        }));
    }, [officeReportsToOptions]);

    return { branchOptions, areOfficesLoading };
}